/**
 * ui-renderers/personal-facts.ts
 * Personal / memory facts panel with pin, delete and memory toggle.
 */

import { dom } from '../dom';
import { escapeHtml, escapeAttr, formatRelativeTime } from './core';

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function _importanceTier(score: number): { label: string; cls: string } {
  if (score >= 0.7) return { label: 'High',   cls: 'pf-imp-high' };
  if (score >= 0.4) return { label: 'Medium', cls: 'pf-imp-medium' };
  return               { label: 'Low',    cls: 'pf-imp-low' };
}

function _cap(s: string) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

const PIN_ICON   = '<svg viewBox="0 0 24 24" fill="currentColor" style="width:13px;height:13px"><path d="M17 4a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v1l2 4v3l-2 1v3h6v-3l-2-1v-3l2-4V4h2zm-5 12v4h-1v-4H7l2-1v-3.18L7 7.46V4h10v3.46L15 11.82V15l2 1h-5z"/></svg>';
const UNPIN_ICON = '<svg viewBox="0 0 24 24" fill="currentColor" style="width:13px;height:13px;opacity:.4"><path d="M17 4a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v1l2 4v3l-2 1v3h6v-3l-2-1v-3l2-4V4h2zm-5 12v4h-1v-4H7l2-1v-3.18L7 7.46V4h10v3.46L15 11.82V15l2 1h-5z"/></svg>';

function _wireMemoryHeader(list: HTMLElement) {
  const toggleBtn = list.querySelector('.ai-mem-toggle-btn');
  if (!toggleBtn) return;
  toggleBtn.addEventListener('click', () => {
    const currentlyEnabled = (toggleBtn as HTMLElement).dataset.enabled === 'true';
    document.dispatchEvent(new CustomEvent('akili:toggle-memory', { detail: !currentlyEnabled }));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function renderPersonalFactsList(facts: any[], snapshot: any = null) {
  const list = dom.pfList;
  if (!list) return;

  const enabled     = snapshot?.memoryEnabled ?? true;
  const description = snapshot?.description  ?? '';
  const topTopics   = snapshot?.topTopics    ?? [];

  const headerHtml = [
    '<div class="ai-mem-header">',
      '<div class="ai-mem-toggle-row">',
        '<span class="ai-mem-toggle-label">',
          enabled ? '🧠 Memory active' : '⏸ Memory paused',
        '</span>',
        '<button class="ai-mem-toggle-btn" ',
          'data-enabled="' + enabled + '" ',
          'title="' + (enabled ? 'Pause memory recording' : 'Resume memory recording') + '" ',
          'aria-label="' + (enabled ? 'Pause memory recording' : 'Resume memory recording') + '">',
          enabled ? 'Pause' : 'Resume',
        '</button>',
      '</div>',
      description
        ? '<div class="ai-mem-description">' + escapeHtml(description) + '</div>'
        : '',
      topTopics.length > 0
        ? '<div class="ai-mem-topics">Topics: ' +
            topTopics.map((t: string) => '<span class="ai-mem-topic-pill">' + escapeHtml(_cap(t)) + '</span>').join('') +
          '</div>'
        : '',
    '</div>',
  ].join('');

  if (!facts || facts.length === 0) {
    list.innerHTML = headerHtml + [
      '<div class="ai-panel-empty">',
        "Kili hasn't stored any facts yet.<br>",
        'Facts are captured automatically as you chat.',
      '</div>',
    ].join('');
    _wireMemoryHeader(list);
    return;
  }

  const rowsHtml = facts.map(fact => {
    const tier    = _importanceTier(fact.importanceScore ?? 0);
    const typeStr = _cap(fact.factType || 'fact');
    const dateStr = fact.createdAt ? formatRelativeTime(fact.createdAt) : '';

    return [
      '<div class="ai-pf-row' + (fact.pinned ? ' ai-pf-pinned' : '') + '" data-pf-id="' + escapeAttr(fact.id) + '">',
        '<div class="ai-pf-row-body">',
          '<div class="ai-pf-row-badges">',
            '<span class="ai-pf-type-badge">' + escapeHtml(typeStr) + '</span>',
            '<span class="ai-pf-imp-badge ' + tier.cls + '">' + tier.label + '</span>',
            fact.pinned ? '<span class="ai-pf-pin-badge" title="Pinned — will never decay">📌 Pinned</span>' : '',
          '</div>',
          '<div class="ai-pf-row-content">' + escapeHtml(fact.content || '') + '</div>',
          dateStr ? '<div class="ai-pf-row-meta">Stored ' + escapeHtml(dateStr) + '</div>' : '',
        '</div>',
        '<div class="ai-pf-row-actions">',
          '<button class="ai-pf-pin-btn" ',
            'data-pf-id="' + escapeAttr(fact.id) + '" ',
            'data-pinned="' + fact.pinned + '" ',
            'title="' + (fact.pinned ? 'Unpin this fact' : 'Pin this fact') + '" ',
            'aria-label="' + (fact.pinned ? 'Unpin' : 'Pin') + '">',
            fact.pinned ? PIN_ICON : UNPIN_ICON,
          '</button>',
          '<button class="ai-pf-del-btn" ',
            'data-pf-id="' + escapeAttr(fact.id) + '" ',
            'title="Remove this fact" aria-label="Remove fact">',
            dom.TRASH_ICON,
          '</button>',
        '</div>',
      '</div>',
    ].join('');
  }).join('');

  const footerHtml = [
    '<div class="ai-mem-footer">',
      '<button class="ai-mem-forget-all-btn" aria-label="Forget all facts">',
        'Forget everything',
      '</button>',
    '</div>',
  ].join('');

  list.innerHTML = headerHtml + rowsHtml + footerHtml;

  _wireMemoryHeader(list);

  list.querySelectorAll('.ai-pf-del-btn').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      document.dispatchEvent(new CustomEvent('akili:delete-personal-fact', {
        detail: (btn as HTMLElement).dataset.pfId,
      }));
    });
  });

  list.querySelectorAll('.ai-pf-pin-btn').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const newPinned = (btn as HTMLElement).dataset.pinned !== 'true';
      document.dispatchEvent(new CustomEvent('akili:toggle-fact-pin', {
        detail: { id: (btn as HTMLElement).dataset.pfId, pinned: newPinned },
      }));
    });
  });

  const forgetBtn = list.querySelector('.ai-mem-forget-all-btn');
  if (forgetBtn) {
    forgetBtn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('akili:reset-memory'));
    });
  }
}
